/* eslint-disable max-lines-per-function */
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { SpecificationStreamProvider } from '../../types/specification';
import type { SpecificationParagraphWithData } from '../../types/specification-gen-content';
import type { SpecificationSection } from '../specification-section';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { create } from '@patsnap/tahiti-core';

export abstract class AbstractBaseSection<T extends SpecificationParagraphWithData = SpecificationParagraphWithData> implements SpecificationSection<T> {
    private readonly onGeneratingEmitter = new Emitter<T>();

    protected currentContent: T | undefined;

    abstract readonly contentType: string;

    beforeGenerate?(regenerate?: boolean): Promise<any> | any;

    generate?(regenerate?: boolean): Promise<any> | any;

    afterGenerate?(regenerate?: boolean): Promise<any> | any;

    updateContent(content: T): T {
        this.onGeneratingEmitter.fire(this.currentContent = content);
        return this.currentContent;
    }

    execute(regenerate?: boolean): SpecificationStreamProvider<T> {
        const deferred = create<T>();

        return {
            subscribe: (callback) => {
                const emitter = this.onGeneratingEmitter.event(callback);

                const started = () => {
                    this.updateContent({
                        type: this.contentType,
                        content: '',
                        status: StreamStatus.PROGRESS_WITHOUT_DATA,
                        children: [],
                    } as unknown as T);
                };

                const generated = () => {
                    deferred.resolve(this.currentContent!);
                };

                const queue = [
                    () => started(),
                    () => this.beforeGenerate?.(regenerate),
                    () => this.generate?.(regenerate),
                    () => this.afterGenerate?.(regenerate),
                    () => generated(),
                ];

                const run = async() => {
                    try {
                        for (const task of queue) {
                            await task();
                        }
                    } catch (error) {
                        Promise.resolve().then(() => deferred.reject(error));
                    } finally {
                        emitter.dispose();
                    }
                };

                run();

                return deferred;
            },
            abort: () => {
                this.stop();
                const content = this.currentContent || {
                    content: '',
                    children: [],
                    type: this.contentType,
                };
                this.updateContent({
                    ...content,
                    status: StreamStatus.ABORTED,
                } as T);
            },
        };
    }

    abstract stop(): any;
}
